import { useState } from 'react';

export default function AboutPage() {
  const [showCallbackModal, setShowCallbackModal] = useState(false);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [showCustomerToolsDropdown, setShowCustomerToolsDropdown] =
    useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const form = e.currentTarget;
      const formDataObj = new FormData(form);

      // Textarea karakter sınırı kontrolü
      const message = formDataObj.get('message') as string;
      if (message && message.length > 500) {
        setSubmitStatus({
          type: 'error',
          message: 'Mesaj 500 karakterden uzun olamaz.',
        });
        setIsSubmitting(false);
        return;
      }

      // Supabase Edge Function'a gönder
      const response = await fetch(
        'https://lbvcnepwlojmojlxmhau.supabase.co/functions/v1/send-contact-email',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            fullName: formDataObj.get('fullName'),
            phone: formDataObj.get('phone'),
            email: formDataObj.get('email'),
            company: formDataObj.get('company'),
            message: formDataObj.get('message'),
          }),
        }
      );

      let result: any;
      try {
        result = await response.json();
      } catch (jsonError) {
        throw new Error('Sunucudan geçerli bir yanıt alınamadı.');
      }

      if (result?.success) {
        setSubmitStatus({
          type: 'success',
          message:
            'Talebiniz başarıyla gönderildi. En kısa sürede sizinle iletişime geçeceğiz.',
        });
        form.reset();
        setTimeout(() => {
          setShowCallbackModal(false);
          setSubmitStatus(null);
        }, 2000);
      } else {
        throw new Error(result?.error || 'Form gönderimi başarısız');
      }
    } catch (error) {
      console.error('Form submit error:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Form gönderilirken bir hata oluştu. Lütfen tekrar deneyin.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setShowServicesDropdown(false);
    setShowCustomerToolsDropdown(false);
    setShowMobileMenu(false);
  };

  const values = [
    {
      icon: 'ri-shield-check-line',
      title: 'Güvenilirlik',
      description:
        'Müşterilerimizin verilerini ve sistemlerini en üst düzeyde koruyoruz.',
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'İnovasyon',
      description:
        'En yeni teknolojileri takip ederek müşterilerimize en iyi çözümleri sunuyoruz.',
    },
    {
      icon: 'ri-team-line',
      title: 'İş Birliği',
      description:
        'Müşterilerimizle uzun vadeli ortaklıklar kurarak birlikte büyüyoruz.',
    },
    {
      icon: 'ri-award-line',
      title: 'Kalite',
      description:
        'Her projede mükemmellik standartlarını koruyarak hizmet veriyoruz.',
    },
  ];

  const successStories = [
    {
      company: 'Ege Lojistik A.Ş.',
      industry: 'Lojistik & Taşımacılık',
      icon: 'ri-truck-line',
      challenge: '200 kullanıcılı şirketin tüm IT altyapısı eskimiş, sık sık kesintiler yaşanıyor ve veri kaybı riski taşıyordu.',
      solution: 'Microsoft 365 geçişi, Veeam yedekleme sistemi kurulumu ve FortiGate güvenlik duvarı entegrasyonu gerçekleştirildi.',
      result: '%99.9 uptime sağlandı, veri kaybı riski sıfıra indirildi, çalışan verimliliği %35 arttı.',
      metric: '%99.9',
      metricLabel: 'Uptime',
      color: 'from-emerald-500 to-teal-600',
      image: 'https://public.readdy.ai/ai/img_res/fcedd1e246d3b2c11bce3f823fadcb9a.jpg',
    },
    {
      company: 'Akdeniz Medikal Ltd.',
      industry: 'Sağlık Sektörü',
      icon: 'ri-hospital-line',
      challenge: 'Hastane yönetim sistemi buluta taşınmak isteniyordu ancak KVKK uyumluluğu ve veri güvenliği kritik öneme sahipti.',
      solution: 'Azure üzerinde özel bulut altyapısı kuruldu, KVKK uyumlu veri yönetimi politikaları oluşturuldu, 7/24 izleme sistemi devreye alındı.',
      result: 'KVKK tam uyumluluğu sağlandı, sistem yanıt süresi %60 iyileşti, yıllık IT maliyeti %40 düştü.',
      metric: '%40',
      metricLabel: 'Maliyet Tasarrufu',
      color: 'from-rose-500 to-pink-600',
      image: 'https://readdy.ai/api/search-image?query=modern%20medical%20clinic%20office%20with%20computers%20showing%20healthcare%20management%20software%2C%20professional%20Turkish%20hospital%20IT%20infrastructure%2C%20clean%20medical%20environment%20with%20technology&width=600&height=400&seq=ss2&orientation=landscape',
    },
    {
      company: 'Kartal Yapı Grubu',
      industry: 'İnşaat & Gayrimenkul',
      icon: 'ri-building-2-line',
      challenge: '5 farklı şehirdeki ofisler arasında güvenli ve hızlı veri paylaşımı sağlanamıyor, proje dosyaları kayboluyordu.',
      solution: 'SD-WAN altyapısı kuruldu, merkezi dosya yönetim sistemi oluşturuldu, tüm ofisler Microsoft 365 ile entegre edildi.',
      result: 'Ofisler arası bağlantı hızı 10 kat arttı, proje teslim süreleri %25 kısaldı, uzaktan çalışma sorunsuz hale geldi.',
      metric: '10x',
      metricLabel: 'Bağlantı Hızı',
      color: 'from-amber-500 to-orange-600',
      image: 'https://readdy.ai/api/search-image?query=modern%20construction%20company%20office%20with%20project%20management%20screens%20and%20architectural%20plans%20on%20monitors%2C%20professional%20Turkish%20real%20estate%20business%20environment%2C%20contemporary%20workspace&width=600&height=400&seq=ss3&orientation=landscape',
    },
    {
      company: 'Pınar Tekstil San.',
      industry: 'Tekstil & Üretim',
      icon: 'ri-t-shirt-line',
      challenge: 'Üretim hattındaki makineler ve ERP sistemi entegre değildi, manuel veri girişi hataları üretimi yavaşlatıyordu.',
      solution: 'Endüstriyel IoT altyapısı kuruldu, ERP sistemi buluta taşındı, gerçek zamanlı üretim izleme paneli oluşturuldu.',
      result: 'Üretim verimliliği %45 arttı, hata oranı %80 azaldı, stok yönetimi tamamen otomatikleşti.',
      metric: '%45',
      metricLabel: 'Verimlilik Artışı',
      color: 'from-violet-500 to-purple-600',
      image: 'https://readdy.ai/api/search-image?query=modern%20textile%20factory%20office%20with%20production%20monitoring%20screens%20and%20ERP%20software%20dashboards%2C%20professional%20Turkish%20manufacturing%20business%20environment%2C%20industrial%20technology%20workspace&width=600&height=400&seq=ss4&orientation=landscape',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white text-gray-900 sticky top-0 z-50 shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-8">
              <a href="/" className="flex items-center space-x-3 cursor-pointer">
                <img
                  src="https://static.readdy.ai/image/ceb81aa66be17b1fc7ed204b9f91964c/7b7be6e694aa19d5c5f7abd74de9f9de.png"
                  alt="Nimbus Bilişim Logo"
                  className="h-24 w-auto"
                />
                <div className="text-lg text-gray-900">
                  Nimbus Bulut Bilişim Teknoloji ve Mühendislik
                </div>
              </a>
              <nav className="hidden lg:flex space-x-8">
                <div className="relative group">
                  <button
                    onClick={() => {
                      setShowServicesDropdown(!showServicesDropdown);
                      setShowCustomerToolsDropdown(false);
                    }}
                    className="flex items-center space-x-1 hover:text-blue-600 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                  >
                    <span>IT Hizmetleri</span>
                    <i
                      className={`ri-arrow-${showServicesDropdown ? 'up' : 'down'}-s-line transition-transform`}
                    ></i>
                  </button>
                  {showServicesDropdown && (
                    <div className="absolute top-full left-0 mt-2 w-[1000px] bg-white rounded-lg shadow-lg border border-gray-200 py-6 z-50">
                      <div className="grid grid-cols-3 gap-8 px-8">
                        {/* IT Destek Hizmetleri */}
                        <div>
                          <h4 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">
                            IT Destek Hizmetleri
                          </h4>
                          <div className="space-y-3">
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/it-support');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                            >
                              <div className="font-medium">IT Destek & Bakım Hizmetleri</div>
                              <div className="text-sm text-gray-500 mt-1">7/24 IT destek ve bakım hizmetleri</div>
                            </button>
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/it-consulting');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                            >
                              <div className="font-medium">IT Danışmanlık Hizmetleri</div>
                              <div className="text-sm text-gray-500 mt-1">Profesyonel danışmanlık</div>
                            </button>
                          </div>
                        </div>

                        {/* Bulut Bilişim Hizmetleri */}
                        <div>
                          <h4 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">
                            Bulut Bilişim Hizmetleri
                          </h4>
                          <div className="space-y-3">
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/virtual-server');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                            >
                              <div className="font-medium">Bulut Bilişim Hizmetleri</div>
                              <div className="text-sm text-gray-500 mt-1">Kurumsal bulut sunucu hizmetleri</div>
                            </button>
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/managed-it');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                            >
                              <div className="font-medium">Yönetilen IT Hizmetleri</div>
                              <div className="text-sm text-gray-500 mt-1">Tüm yönetim hizmetleri</div>
                            </button>
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/migration');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                            >
                              <div className="font-medium">Sistem Taşıma ve Geçiş Hizmetleri (Migration)</div>
                              <div className="text-sm text-gray-500 mt-1">Sunucu taşıma hizmetleri</div>
                            </button>
                          </div>
                        </div>

                        {/* Microsoft 365 Hizmetleri */}
                        <div>
                          <h4 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">
                            Microsoft 365 Hizmetleri
                          </h4>
                          <div className="space-y-3">
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/microsoft-365');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                            >
                              <div className="font-medium">Microsoft 365 Geçiş ve Yönetimi</div>
                              <div className="text-sm text-gray-500 mt-1">Azure Expert hizmetleri</div>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/')}
                  className="hover:text-blue-600 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                >
                  Başarı Hikayeleri
                </button>
                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/about')}
                  className="hover:text-blue-600 transition-colors whitespace-nowrap text-blue-600 font-semibold cursor-pointer"
                >
                  Hakkımızda
                </button>
                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/')}
                  className="hover:text-blue-600 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                >
                  SSS
                </button>
              </nav>
            </div>
            <div className="flex items-center space-x-4">
              <div className="hidden lg:flex items-center space-x-4 ml-8">
                <div className="relative group">
                  <button
                    onClick={() => {
                      setShowCustomerToolsDropdown(!showCustomerToolsDropdown);
                      setShowServicesDropdown(false);
                    }}
                    className="flex items-center space-x-1 hover:text-blue-600 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                  >
                    <span>Müşteri Araçları</span>
                    <i
                      className={`ri-arrow-${showCustomerToolsDropdown ? 'up' : 'down'}-s-line transition-transform`}
                    ></i>
                  </button>
                  {showCustomerToolsDropdown && (
                    <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                      <button
                        onClick={() => window.REACT_APP_NAVIGATE('/portal')}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700 cursor-pointer"
                      >
                        <div className="font-medium">Müşteri Portalı</div>
                        <div className="text-sm text-gray-500">
                          Fatura ve destek yönetimi
                        </div>
                      </button>
                      <a
                        href="mailto:destek@nimbusbilisim.com"
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700 cursor-pointer block"
                      >
                        <div className="font-medium">7/24 Destek Hattı</div>
                        <div className="text-sm text-gray-500">
                          destek@nimbusbilisim.com
                        </div>
                      </a>
                      <a
                        href="https://rustdesk.com/download"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700 cursor-pointer block"
                      >
                        <div className="font-medium">RustDesk İndir</div>
                        <div className="text-sm text-gray-500">
                          Uzaktan destek programı
                        </div>
                      </a>
                      <button
                        onClick={() => {
                          setShowCallbackModal(true);
                          setShowCustomerToolsDropdown(false);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700 cursor-pointer"
                      >
                        <div className="font-medium">Geri Arama Talebi</div>
                        <div className="text-sm text-gray-500">
                          Uzmanlarımızla görüşün
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowCallbackModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                İletişim
              </button>
              <a
                href="https://www.linkedin.com/company/nimbus-bulut-bili%C5%9Fim-teknoloji-yaz%C4%B1l%C4%B1m-ve-m%C3%BChendislik-a-%C5%9F/?viewAsMember=true"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer"
              >
                <i className="ri-linkedin-fill text-2xl"></i>
              </a>
              <button
                onClick={() => setShowMobileMenu(true)}
                className="lg:hidden text-gray-700 cursor-pointer"
              >
                <i className="ri-menu-line text-2xl"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Click outside to close dropdowns */}
      {(showServicesDropdown || showCustomerToolsDropdown) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowServicesDropdown(false);
            setShowCustomerToolsDropdown(false);
          }}
        ></div>
      )}

      {/* Mobile Menu */}
      {showMobileMenu && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowMobileMenu(false)}
          ></div>
          <div className="absolute right-0 top-0 h-full w-80 bg-white shadow-xl overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-xl font-bold text-gray-900">Menü</h3>
                <button
                  onClick={() => setShowMobileMenu(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <nav className="space-y-6">
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                    IT Hizmetleri
                  </h4>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/services/it-support');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">
                        IT Destek & Bakım Hizmetleri
                      </div>
                      <div className="text-sm text-gray-500">
                        7/24 IT destek ve bakım hizmetleri
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/services/it-consulting');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">IT Danışmanlık Hizmetleri</div>
                      <div className="text-sm text-gray-500">
                        Profesyonel danışmanlık
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/services/virtual-server');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">Bulut Bilişim Hizmetleri</div>
                      <div className="text-sm text-gray-500">
                        Kurumsal bulut sunucu hizmetleri
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/services/managed-it');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">Yönetilen IT Hizmetleri</div>
                      <div className="text-sm text-gray-500">
                        Tüm yönetim hizmetleri
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/services/microsoft-365');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">Microsoft 365 Geçiş ve Yönetimi</div>
                      <div className="text-sm text-gray-500">
                        Azure Expert hizmetleri
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/services/migration');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">Sistem Taşıma ve Geçiş</div>
                      <div className="text-sm text-gray-500">
                        Sunucu taşıma hizmetleri
                      </div>
                    </button>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                    Şirket
                  </h4>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      Başarı Hikayeleri
                    </button>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/about');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-blue-600 font-semibold cursor-pointer"
                    >
                      Hakkımızda
                    </button>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      SSS
                    </button>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                    Müşteri Araçları
                  </h4>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/portal');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">Müşteri Portalı</div>
                      <div className="text-sm text-gray-500">
                        Fatura ve destek yönetimi
                      </div>
                    </button>
                    <a
                      href="mailto:destek@nimbusbilisim.com"
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700 cursor-pointer block"
                    >
                      <div className="font-medium">7/24 Destek Hattı</div>
                      <div className="text-sm text-gray-500">
                        destek@nimbusbilisim.com
                      </div>
                    </a>
                    <a
                      href="https://rustdesk.com/download"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700 cursor-pointer block"
                    >
                      <div className="font-medium">RustDesk İndir</div>
                      <div className="text-sm text-gray-500">
                        Uzaktan destek programı
                      </div>
                    </a>
                    <button
                      onClick={() => {
                        setShowCallbackModal(true);
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">Geri Arama Talebi</div>
                      <div className="text-sm text-gray-500">
                        Uzmanlarımızla görüşün
                      </div>
                    </button>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <button
                    onClick={() => {
                      setShowCallbackModal(true);
                      setShowMobileMenu(false);
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                  >
                    İletişim
                  </button>
                </div>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white py-20 overflow-hidden">
        <div
          className="absolute inset-0 w-full h-full bg-cover bg-center opacity-30"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=modern%20Turkish%20technology%20company%20office%20environment%2C%20professional%20team%20workspace%20with%20advanced%20IT%20infrastructure%2C%20contemporary%20business%20setting%20with%20blue%20lighting&width=1920&height=800&seq=24&orientation=landscape')`,
            animation: 'slowPan 5s ease-in-out infinite alternate'
          }}
        ></div>
        <style>{`
          @keyframes slowPan {
            0% {
              transform: scale(1) translateY(0);
            }
            100% {
              transform: scale(1.1) translateY(-3%);
            }
          }
        `}</style>
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-4xl">
            <p className="text-blue-400 text-lg mb-4">
              Türkiye'nin Güvenilir IT Partneri
            </p>
            <h1 className="text-5xl lg-text-6xl font-bold mb-6">
              Nimbus Bilişim Hakkında
            </h1>
            <p className="text-xl mb-8 leading-relaxed">
              2025 yılından bu yana Türkiye'de faaliyet gösteren Nimbus Bilişim,
              işletmelerin dijital dönüşüm yolculuğunda güvenilir teknoloji
              partneri olarak hizmet vermektedir. Microsoft, Fortinet ve Veeam
              iş ortaklarımızla sektörde öncü konumdayız.
            </p>
            <button
              onClick={() => setShowCallbackModal(true)}
              className="bg-blue-600 hover:bg-blue-700 px-8 py-4 rounded-lg text-lg font-semibold transition-colors flex items-center space-x-2 whitespace-nowrap cursor-pointer"
            >
              <span>Bizimle İletişime Geçin</span>
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Company Story Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg-grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Hikayemiz
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Nimbus Bilişim, İzmir'de küçük bir ekip ile kuruldu.
                  Kurucu ortaklarımızın 20+ yıllık IT sektörü deneyimi ve
                  müşteri odaklı yaklaşımı ile başlayan yolculuğumuz, bugün
                  Türkiye'nin önde gelen IT çözüm ortaklarından biri haline geldi.
                </p>
                <p>
                  İlk günden itibaren amacımız, işletmelerin teknoloji ihtiyaçlarını
                  en üst düzeyde karşılamak ve dijital dönüşüm süreçlerinde
                  güvenilir bir partner olmaktı. Bu vizyonla hareket ederek,
                  Microsoft, Fortinet ve Veeam gibi dünya lideri teknoloji
                  üreticileriyle stratejik iş ortaklıkları kurduk.
                </p>
                <p>
                  Bugün 100'den fazla müşterimize hizmet veriyor, KOBİ'lerden büyük
                  kurumlara kadar geniş bir yelpazede IT çözümleri sunuyoruz. Ekibimiz
                  sürekli gelişen teknolojileri takip ederek, müşterilerimize en
                  güncel ve etkili çözümleri sunmaya devam ediyor.
                </p>
              </div>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=modern%20Turkish%20IT%20company%20office%20interior%2C%20professional%20team%20working%20environment%20with%20computers%20and%20technology%20equipment%2C%20contemporary%20workspace%20design&width=600&height=500&seq=25&orientation=landscape"
                alt="Nimbus Bilişim Ofisi"
                className="w-full rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Değerlerimiz
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Nimbus Bilişim olarak, her projede ve müşteri ilişkisinde rehber
              aldığımız temel değerlerimiz
            </p>
          </div>
          <div className="grid md-grid-cols-2 lg-grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-6 text-center shadow-sm hover-shadow-md transition-shadow"
              >
                <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className={`${value.icon} text-blue-600 text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <span className="inline-block bg-emerald-50 text-emerald-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 tracking-wide uppercase">Referanslarımız</span>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Başarı Hikayelerimiz</h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              Müşterilerimizin dijital dönüşüm yolculuklarında nasıl fark yarattığımızı keşfedin.
            </p>
          </div>

          <div className="space-y-12">
            {successStories.map((story, index) => (
              <div
                key={index}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-0 rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 border border-gray-100`}
              >
                {/* Image */}
                <div className="lg:w-2/5 relative overflow-hidden">
                  <img
                    src={story.image}
                    alt={story.company}
                    className="w-full h-64 lg:h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  {/* Metric Badge */}
                  <div className="absolute bottom-6 left-6">
                    <div className={`bg-gradient-to-br ${story.color} text-white rounded-xl px-5 py-3 shadow-lg`}>
                      <div className="text-3xl font-extrabold leading-none">{story.metric}</div>
                      <div className="text-xs font-medium opacity-90 mt-0.5">{story.metricLabel}</div>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="lg:w-3/5 bg-white p-8 lg:p-10 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-5">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-lg bg-gradient-to-br ${story.color} text-white`}>
                      <i className={`${story.icon} text-lg`}></i>
                    </div>
                    <div>
                      <div className="font-bold text-gray-900 text-lg leading-tight">{story.company}</div>
                      <div className="text-sm text-gray-400">{story.industry}</div>
                    </div>
                  </div>

                  <div className="space-y-5">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-red-50 text-red-500 mt-0.5">
                        <i className="ri-error-warning-line text-base"></i>
                      </div>
                      <div>
                        <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Sorun</div>
                        <p className="text-gray-600 text-sm leading-relaxed">{story.challenge}</p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-blue-50 text-blue-500 mt-0.5">
                        <i className="ri-settings-3-line text-base"></i>
                      </div>
                      <div>
                        <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Çözüm</div>
                        <p className="text-gray-600 text-sm leading-relaxed">{story.solution}</p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-emerald-50 text-emerald-500 mt-0.5">
                        <i className="ri-checkbox-circle-line text-base"></i>
                      </div>
                      <div>
                        <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Sonuç</div>
                        <p className="text-gray-600 text-sm leading-relaxed">{story.result}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-14 text-center">
            <p className="text-gray-500 mb-5 text-base">Siz de başarı hikayelerimizin bir parçası olmak ister misiniz?</p>
            <button
              onClick={() => setShowCallbackModal(true)}
              className="inline-flex items-center gap-2 bg-gray-900 hover:bg-gray-700 text-white px-8 py-3.5 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <span>Ücretsiz Danışmanlık Alın</span>
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Sertifikalarımız ve İş Ortaklarımız
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Dünya lideri teknoloji üreticileriyle kurduğumuz stratejik iş
              ortaklıkları
            </p>
          </div>
          
          {/* First Row - 4 Partners */}
          <div className="grid md-grid-cols-2 lg-grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg p-8 text-center shadow-sm hover-shadow-md transition-shadow">
              <img
                src="https://static.readdy.ai/image/ceb81aa66be17b1fc7ed204b9f91964c/a9b14aac8bb574401cbbd7a11dd9a2a9.png"
                alt="Microsoft Partner"
                className="w-32 h-16 object-contain mx-auto mb-4"
              />
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Microsoft Partner
              </h3>
              <p className="text-gray-600">
                Microsoft 365, Azure ve Modern Workplace çözümlerinde sertifikalı
                iş ortağıyız.
              </p>
            </div>
            <div className="bg-white rounded-lg p-8 text-center shadow-sm hover-shadow-md transition-shadow">
              <img
                src="https://static.readdy.ai/image/ceb81aa66be17b1fc7ed204b9f91964c/098fe1520bd15c6029758151699f3c82.png"
                alt="Fortinet Partner"
                className="w-32 h-16 object-contain mx-auto mb-4"
              />
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Fortinet Partner
              </h3>
              <p className="text-gray-600">
                FortiGate güvenlik duvarı ve siber güvenlik çözümlerinde uzman
                iş ortağıyız.
              </p>
            </div>
            <div className="bg-white rounded-lg p-8 text-center shadow-sm hover-shadow-md transition-shadow">
              <img
                src="https://static.readdy.ai/image/ceb81aa66be17b1fc7ed204b9f91964c/7130c45859039f94fa4c5ffba2516bcb.png"
                alt="Veeam Partner"
                className="w-32 h-16 object-contain mx-auto mb-4"
              />
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Veeam Partner
              </h3>
              <p className="text-gray-600">
                Yedekleme ve felaket kurtarma çözümlerinde sertifikalı iş
                ortağıyız.
              </p>
            </div>
            <div className="bg-white rounded-lg p-8 text-center shadow-sm hover-shadow-md transition-shadow">
              <img
                src="https://static.readdy.ai/image/ceb81aa66be17b1fc7ed204b9f91964c/b0a304e1c36db2e761db210ed747cc22.png"
                alt="VMware Partner"
                className="w-32 h-16 object-contain mx-auto mb-4"
              />
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                VMware Partner
              </h3>
              <p className="text-gray-600">
                HP sunucu, depolama ve ağ çözümlerinde yetkili iş ortağıyız.
              </p>
            </div>
          </div>

          {/* Second Row - 4 Partners */}
          <div className="grid md-grid-cols-2 lg-grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg p-8 text-center shadow-sm hover-shadow-md transition-shadow">
              <img
                src="https://static.readdy.ai/image/ceb81aa66be17b1fc7ed204b9f91964c/24772735b2b2e0a7af08d80878a1941e.png"
                alt="Huawei Partner"
                className="w-32 h-16 object-contain mx-auto mb-4"
              />
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Huawei Partner
              </h3>
              <p className="text-gray-600">
                Huawei ağ altyapısı ve telekomünikasyon çözümlerinde uzman iş
                ortağıyız.
              </p>
            </div>
            <div className="bg-white rounded-lg p-8 text-center shadow-sm hover-shadow-md transition-shadow">
              <img
                src="https://static.readdy.ai/image/ceb81aa66be17b1fc7ed204b9f91964c/163dc45fea1843cdf935d3adc07e4c9f.png"
                alt="HP Partner"
                className="w-32 h-16 object-contain mx-auto mb-4"
              />
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                HP Partner
              </h3>
              <p className="text-gray-600">
                HP sunucu, depolama ve ağ çözümlerinde yetkili iş ortağıyız.
              </p>
            </div>
            <div className="bg-white rounded-lg p-8 text-center shadow-sm hover-shadow-md transition-shadow">
              <img
                src="https://static.readdy.ai/image/ceb81aa66be17b1fc7ed204b9f91964c/92100641411b150fe44d3206eacc5156.png"
                alt="Lenovo Partner"
                className="w-32 h-16 object-contain mx-auto mb-4"
              />
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Lenovo Partner
              </h3>
              <p className="text-gray-600">
                Lenovo sunucu ve iş istasyonu çözümlerinde yetkili satıcıyız.
              </p>
            </div>
            <div className="bg-white rounded-lg p-8 text-center shadow-sm hover-shadow-md transition-shadow">
              <img
                src="https://static.readdy.ai/image/ceb81aa66be17b1fc7ed204b9f91964c/72a1120c9b2ef4aae168a3905967a8ec.png"
                alt="Trend Micro Partner"
                className="w-32 h-16 object-contain mx-auto mb-4"
              />
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Trend Micro Partner
              </h3>
              <p className="text-gray-600">
                Siber güvenlik ve endpoint koruma çözümlerinde uzman iş
                ortağıyız.
              </p>
            </div>
          </div>

          {/* Third Row - 4 Partners */}
          <div className="grid md-grid-cols-2 lg-grid-cols-4 gap-6">
            <div className="bg-white rounded-lg p-8 text-center shadow-sm hover-shadow-md transition-shadow">
              <img
                src="https://static.readdy.ai/image/ceb81aa66be17b1fc7ed204b9f91964c/7e98c9d0a4267d8755996d4716120420.png"
                alt="Zyxel Partner"
                className="w-32 h-16 object-contain mx-auto mb-4"
              />
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Zyxel Partner
              </h3>
              <p className="text-gray-600">
                Zyxel ağ çözümleri ve güvenlik ürünlerinde yetkili iş ortağıyız.
              </p>
            </div>
            <div className="bg-white rounded-lg p-8 text-center shadow-sm hover-shadow-md transition-shadow">
              <img
                src="https://static.readdy.ai/image/ceb81aa66be17b1fc7ed204b9f91964c/19b2e129dac8b12e82b8de5993c48589.png"
                alt="Aruba Partner"
                className="w-32 h-16 object-contain mx-auto mb-4"
              />
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Aruba Partner
              </h3>
              <p className="text-gray-600">
                Aruba kablosuz ağ ve SD-WAN çözümlerinde uzman iş ortağıyız.
              </p>
            </div>
            <div className="bg-white rounded-lg p-8 text-center shadow-sm hover-shadow-md transition-shadow">
              <img
                src="https://static.readdy.ai/image/ceb81aa66be17b1fc7ed204b9f91964c/89fa209228351aa5f1dbd61452106b7c.png"
                alt="Dell Partner"
                className="w-32 h-16 object-contain mx-auto mb-4"
              />
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Dell Partner
              </h3>
              <p className="text-gray-600">
                Dell sunucu, depolama ve altyazısı çözümlerinde uzman iş ortağıyız.
              </p>
            </div>
            <div className="bg-white rounded-lg p-8 text-center shadow-sm hover-shadow-md transition-shadow">
              <img
                src="https://static.readdy.ai/image/ceb81aa66be17b1fc7ed204b9f91964c/d8f94f3e8f87faf14cc72a241053fa29.png"
                alt="Proxmox Partner"
                className="w-32 h-16 object-contain mx-auto mb-4"
              />
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Proxmox Partner
              </h3>
              <p className="text-gray-600">
                Proxmox sanallaştırma ve konteyner çözümlerinde uzman iş
                ortağıyız.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="py-16 bg-blue-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Nimbus Bilişim ile Tanışın
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            IT ihtiyaçlarınız için güvenilir bir partner arıyorsanız, deneyimli
            ekibimizle tanışmak için bizimle iletişime geçin.
          </p>
          <button
            onClick={() => setShowCallbackModal(true)}
            className="bg-white hover:bg-gray-100 text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold transition-colors flex items-center space-x-2 mx-auto whitespace-nowrap cursor-pointer"
          >
            <span>Bizimle İletişime Geçin</span>
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white text-gray-900 py-12 border-t border-gray-200">
        <div className="container mx-auto px-4">
          <div className="grid md-grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <img
                  src="https://static.readdy.ai/image/ceb81aa66be17b1fc7ed204b9f91964c/7b7be6e694aa19d5c5f7abd74de9f9de.png"
                  alt="Nimbus Bilişim Logo"
                  className="h-24 w-auto"
                />
                <div className="text-lg">Nimbus Bulut Bilişim Teknoloji ve Mühendislik</div>
              </div>
              <p className="text-gray-600 mb-4">
                Türkiye'nin güvenilir IT çözüm ortağı. İşletmenizin teknoloji
                ihtiyaçlarında yanımızdayız.
              </p>
              <div className="flex space-x-4">
                <a
                  href="https://www.linkedin.com/company/nimbus-bulut-bili%C5%9Fim-teknoloji-yaz%C4%B1l%C4%B1m-ve-m%C3%BChendislik-a-%C5%9F/?viewAsMember=true"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-blue-600 transition-colors"
                >
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Hizmetler</h4>
              <ul className="space-y-2 text-gray-600">
                <li>
                  <button
                    onClick={() =>
                      window.REACT_APP_NAVIGATE('/services/virtual-server')
                    }
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    Sanal Sunucu
                  </button>
                </li>
                <li>
                  <button
                    onClick={() =>
                      window.REACT_APP_NAVIGATE('/services/microsoft-365')
                    }
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    Microsoft 365
                  </button>
                </li>
                <li>
                  <button
                    onClick={() =>
                      window.REACT_APP_NAVIGATE('/services/it-support')
                    }
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    IT Destek
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/')}
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    İş Ortaklıkları
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Şirket</h4>
              <ul className="space-y-2 text-gray-600">
                <li>
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/about')}
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    Hakkımızda
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/')}
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    Başarı Hikayeleri
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/')}
                    className="hover:text-blue-600 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                  >
                    SSS
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/')}
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    Müşteri Portalı
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">İletişim</h4>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center space-x-2">
                  <i className="ri-mail-line"></i>
                  <span>info@nimbusbilisim.com</span>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="ri-map-pin-line"></i>
                  <span>İzmir, Türkiye</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-200 mt-10 pt-10 text-center text-gray-600">
            <p>
              © 2025 Nimbus Bulut Bilişim Teknoloji ve Mühendislik. Tüm hakları saklıdır. |{' '}
              <a
                href="https://readdy.ai/?origin=logo"
                className="hover:text-blue-600 transition-colors"
              >
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* Callback Modal */}
      {showCallbackModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <img
                    src="https://static.readdy.ai/image/ceb81aa66be17b1fc7ed204b9f91964c/7b7be6e694aa19d5c5f7abd74de9f9de.png"
                    alt="Nimbus Bilişim Logo"
                    className="h-12 w-auto"
                  />
                  <h3 className="text-2xl font-bold text-gray-900">
                    İletişim Formu
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowCallbackModal(false);
                    setSubmitStatus(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              {submitStatus && (
                <div
                  className={`mb-6 p-4 rounded-lg ${
                    submitStatus.type === 'success'
                      ? 'bg-green-50 text-green-800 border border-green-200'
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}
                >
                  {submitStatus.message}
                </div>
              )}

              <form
                onSubmit={handleFormSubmit}
                data-readdy-form
                id="about-contact-form"
              >
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="fullName"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Ad Soyad *
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus-ring-2 focus-ring-blue-500 focus-border-transparent text-sm"
                      placeholder="Adınız ve soyadınız"
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Telefon *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus-ring-2 focus-ring-blue-500 focus-border-transparent text-sm"
                      placeholder="Telefon numaranız"
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      E-posta *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus-ring-2 focus-ring-blue-500 focus-border-transparent text-sm"
                      placeholder="E-posta adresiniz"
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="company"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Şirket
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus-ring-2 focus-ring-blue-500 focus-border-transparent text-sm"
                      placeholder="Şirket adınız (opsiyonel)"
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Mesaj
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus-ring-2 focus-ring-blue-500 focus-border-transparent text-sm resize-none"
                      placeholder="Mesajınızı buraya yazın (maksimum 500 karakter)"
                      onChange={handleInputChange}
                    ></textarea>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 hover:bg-blue-7
                  00 disabled-bg-blue-400 text-white py-3 rounded-lg transition-colors mt-6 whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Gönderiliyor...' : 'Gönder'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
